import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import oswald700 from "@fontsource/oswald/files/oswald-latin-700-normal.woff2?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { Header } from "../components/site/Header";
import { Footer } from "../components/site/Footer";
import { MobileStickyBar } from "../components/site/MobileStickyBar";
import { CallTracking } from "../components/site/CallTracking";
import { Analytics } from "../components/site/Analytics";
import { BUSINESS } from "../components/site/constants";
import { SITE_URL, ROOFING_CONTRACTOR_ID, OWNER_ID, WEBSITE_ID } from "../components/site/site-config";

function NotFoundComponent() {
  return (
    <>
      <Header />
      <main className="flex min-h-[60vh] items-center justify-center bg-background px-4">
        <div className="max-w-md text-center py-16">
          <h1 className="text-7xl font-display font-bold text-brand">404</h1>
          <h2 className="mt-4 text-xl font-semibold">Page not found</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            The page you're looking for doesn't exist. Try one of our{" "}
            <Link to="/services" className="text-brand underline">roofing services</Link>,{" "}
            <Link to="/blog" className="text-brand underline">the blog</Link>, or{" "}
            <Link to="/contact" className="text-brand underline">contact us</Link>.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/" className="btn-cta">Go home</Link>
            <a href={BUSINESS.phoneHref} className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium min-h-12">
              Call {BUSINESS.phone}
            </a>
          </div>
        </div>
      </main>
      <Footer />
      <MobileStickyBar />
    </>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">This page didn't load</h1>
        <p className="mt-2 text-sm text-muted-foreground">Something went wrong. Try again or call {BUSINESS.phone}.</p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button onClick={() => { router.invalidate(); reset(); }} className="btn-cta">Try again</button>
          <a href="/" className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium">Go home</a>
        </div>
      </div>
    </div>
  );
}

const ROOF_CONTRACTOR_GRAPH = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebSite",
      "@id": WEBSITE_ID,
      url: SITE_URL,
      name: "Destination Roofing Inc.",
      publisher: { "@id": ROOFING_CONTRACTOR_ID },
      inLanguage: "en-US",
    },
    {
      "@type": "RoofingContractor",
      "@id": ROOFING_CONTRACTOR_ID,
      name: "Destination Roofing Inc.",
      legalName: "Destination Roofing Inc.",
      url: SITE_URL,
      telephone: BUSINESS.phone,
      email: BUSINESS.email,
      description:
        "Family-owned, licensed and insured roofing contractor serving Citrus County, FL. Roof replacement, roof repair, metal, shingle and flat roofing, gutters, soffit and third-nail wind mitigation.",
      areaServed: { "@type": "AdministrativeArea", name: "Citrus County, FL" },
      identifier: {
        "@type": "PropertyValue",
        propertyID: "Florida Roofing Contractor License",
        value: BUSINESS.licenseNumber,
      },
      founder: { "@id": OWNER_ID },
      openingHoursSpecification: [
        {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
          opens: "06:30",
          closes: "17:00",
        },
      ],
      openingHours: "Mo-Sa 06:30-17:00",
      sameAs: [BUSINESS.facebook],
      address: {
        "@type": "PostalAddress",
        streetAddress: "246 W Casurina Pl",
        addressLocality: "Beverly Hills",
        addressRegion: "FL",
        postalCode: "34465",
        addressCountry: "US",
      },
    },
  ],
};

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "robots", content: "index,follow,max-image-preview:large" },
      { name: "author", content: "Destination Roofing Inc." },
      { property: "og:site_name", content: "Destination Roofing Inc." },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "en_US" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", href: "/favicon.ico" },
      { rel: "preload", as: "font", type: "font/woff2", href: oswald700, crossOrigin: "anonymous" } as unknown as { rel: string; href: string },
    ],
    scripts: [
      {
        children:
          "window.dataLayer = window.dataLayer || [];function gtag(){dataLayer.push(arguments);}gtag('js', new Date());gtag('config', 'GT-TQTFG7QG', {send_page_view: false});gtag('config', 'AW-18330308052');gtag('config', 'AW-18330308052/jmlVCKeN7NMcENSbyaRE', {'phone_conversion_number': '(352) 901-3094','phone_conversion_callback': function(n){ if(!n) return; window.__drForwardingNumber = n; if (typeof window.__drApplyForwarding === 'function') window.__drApplyForwarding(n); }});(function(){var loaded=false;function loadTag(){if(loaded)return;loaded=true;var s=document.createElement('script');s.async=true;s.src='https://www.googletagmanager.com/gtag/js?id=GT-TQTFG7QG';document.head.appendChild(s);}['click','keydown','touchstart','pointerdown','scroll'].forEach(function(e){window.addEventListener(e,loadTag,{once:true,passive:true});});window.addEventListener('pagehide',loadTag);if('requestIdleCallback' in window){requestIdleCallback(loadTag,{timeout:2500});}else{setTimeout(loadTag,2000);}})();",
      },

      {
        type: "application/ld+json",
        children: JSON.stringify(ROOF_CONTRACTOR_GRAPH),
      },
    ],

  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en-US">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <CallTracking />
      <Analytics />
      <Header />
      <main>
        <Outlet />
      </main>
      <Footer />
      <MobileStickyBar />
    </QueryClientProvider>
  );
}
